import React, { useEffect, useState } from "react";
import { Alert, Box, Button, Chip, Typography } from "@mui/material";
import { fetchClassCertificateTypes, saveClassCertificateTypes } from "../../api/apiClient";
import CertificateTypesPicker from "./CertificateTypesPicker";

/**
 * The certificates a class issues. Coordinators and admins change the list (from the center's enabled types);
 * instructors see it. Calls {@code onChanged} after a save so AHA sections can appear or disappear.
 * {@code excludeTypes} are managed elsewhere on the page (AHA has its own section): they are not offered here and
 * are kept exactly as saved when this list is saved. {@code refreshKey} reloads after those are changed.
 */
export default function ClassCertificatesPanel({ classId, canEdit, onChanged, excludeTypes = [], refreshKey }) {
  const [data, setData] = useState(null);
  const [draft, setDraft] = useState([]);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);

  const visible = (types) => (types || []).filter((t) => !excludeTypes.includes(t));

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    let active = true;
    fetchClassCertificateTypes(classId)
      .then((d) => { if (active) { setData(d); setDraft(visible(d?.types)); } })
      .catch(() => { if (active) setError("This class's certificates couldn't be loaded."); });
    return () => { active = false; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [classId, refreshKey]); // excludeTypes is a fixed list

  if (error) return <Alert severity="warning" sx={{ mb: 2 }}>{error}</Alert>;
  if (!data) return null;

  const hiddenSaved = (data.types || []).filter((t) => excludeTypes.includes(t));
  const changed = JSON.stringify(draft) !== JSON.stringify(visible(data.types));
  const nameOf = (type) => (data.available || []).find((a) => a.type === type)?.name || type;

  const save = async () => {
    setSaving(true);
    setMessage(null);
    try {
      const order = (data.available || []).map((a) => a.type);
      const types = [...new Set([...draft, ...hiddenSaved])].sort((a, b) => order.indexOf(a) - order.indexOf(b));
      const saved = await saveClassCertificateTypes(classId, types);
      setData(saved);
      setDraft(visible(saved.types));
      setMessage({ severity: "success", text: "Certificates saved." });
      onChanged?.(saved.types || []);
    } catch (e) {
      setMessage({ severity: "error", text: e?.response?.data?.message || "The certificates couldn't be saved." });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box data-testid="class-certificates" sx={{ p: 2, mb: 2, border: "1px solid", borderColor: "divider", borderRadius: 2 }}>
      {message && <Alert severity={message.severity} sx={{ mb: 1 }} onClose={() => setMessage(null)}>{message.text}</Alert>}
      {canEdit ? (
        <>
          <CertificateTypesPicker available={(data.available || []).filter((a) => !excludeTypes.includes(a.type))}
            value={draft} onChange={setDraft} disabled={saving} warnWhenEmpty={hiddenSaved.length === 0} />
          {changed && (
            <Box sx={{ mt: 1, display: "flex", gap: 1 }}>
              <Button size="small" variant="contained" onClick={save} disabled={saving}>Save certificates</Button>
              <Button size="small" onClick={() => setDraft(visible(data.types))} disabled={saving}>Cancel</Button>
            </Box>
          )}
        </>
      ) : (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
          <Typography sx={{ fontSize: 13, fontWeight: 700 }}>Certificates issued</Typography>
          {(data.types || []).length === 0
            ? <Typography variant="body2" color="text.secondary">None</Typography>
            : data.types.map((t) => <Chip key={t} size="small" label={nameOf(t)} />)}
        </Box>
      )}
    </Box>
  );
}
